/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHistory } from 'react-router-dom'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiPanel,
  EuiSpacer,
  EuiText,
  useEuiTheme,
} from '@elastic/eui'

import {
  useGetBasePricesV1,
  useGetInstanceChartDataV2,
  useGetInstanceCostItemsV2,
} from '@modules/billing-lib/hooks'
import { CuiAlert } from '@modules/cui/Alert'
import { processQueryParams } from '@modules/billing-api/utils'
import { colorForInstances } from '@modules/billing-lib/colorGenerator'

import MainFilters from '@/components/User/BillingUsage/components/Filters/MainFilters'
import messages from '@/components/User/BillingOverviewV2/messages'
import ProductItemList from '@/components/User/BillingUsage/BillingUsageByInstance/ProductItemListTable'
import {
  buildServerlessDimensionItems,
  mapToDeploymentCostsItemsV2,
} from '@/components/User/BillingUsage/BillingUsageByInstance/utils'
import { ViewByToBucketingStrategy } from '@/components/User/BillingUsage/constants'
import InstanceUsageTable from '@/components/User/BillingUsage/components/InstanceUsageTable'
import ErrorCallout from '@/components/User/BillingUsage/components/ErrorCallout'
import { ProductType } from '@/components/User/BillingUsage/types'
import DocLink from '@/components/DocLink'

import { Action } from '../components/Filters/filtersReducer'
import { getValidationError, stringifyFilters } from '../components/Filters/utils'
import CostsChartByInstance from '../components/CostsChartByInstance'
import withFiltersProvider from '../components/Filters/withFiltersProvider'
import useFilters from '../components/Filters/useFilters'
import { billingResponsivePanelCss, costChartPanelCss } from '../styles'

import type { AllProps } from '@/components/User/BillingUsage/BillingUsageByInstance/types'
import type { FunctionComponent } from 'react'

const BillingUsageByInstance: FunctionComponent<AllProps> = ({
  organizationId,
  instanceId,
  instanceName,
}) => {
  const history = useHistory()
  const { filters, dispatch } = useFilters()
  const theme = useEuiTheme()

  const areDatesValid = !getValidationError({
    startDate: filters.startDate,
    endDate: filters.endDate,
    viewBy: filters.viewBy,
  })
  const { data: base_prices = [] } = useGetBasePricesV1()

  // Each time the filters change we want to update queryParams
  useEffect(() => {
    colorForInstances.reset()
    const queryString = stringifyFilters(filters)
    history.replace(`${history.location.pathname}?${queryString}`)
  }, [history, filters])

  const onBackToUsageClick = () =>
    dispatch({ type: Action.SET_INSTANCE_ID, payload: { instanceId: undefined } })

  const instanceCostItems = useGetInstanceCostItemsV2(
    {
      pathParameters: {
        organization_id: organizationId,
        instance_id: instanceId,
      },
      queryParameters: processQueryParams({
        from: filters.startDate.startOf('day').utc().format(),
        to: filters.endDate.endOf('day').utc().format(),
      }),
    },
    {
      enabled: areDatesValid,
    },
  )

  const instance_type = instanceCostItems.data?.products[0]?.type

  const renderListTable = () => {
    if (instanceCostItems.isError) {
      return (
        <CuiAlert type='error' data-test-id='error-message'>
          <FormattedMessage {...messages.genericErrorMessage} />
        </CuiAlert>
      )
    }

    if (instance_type === 'deployment') {
      return (
        <EuiPanel
          hasShadow={false}
          paddingSize='l'
          hasBorder={true}
          css={billingResponsivePanelCss}
          data-test-id='deployment-usage-table'
        >
          <InstanceUsageTable itemsCosts={mapToDeploymentCostsItemsV2(instanceCostItems.data)} />
        </EuiPanel>
      )
    }

    return (
      <EuiFlexGroup data-test-id='billing-usage.product-list-table'>
        <ProductItemList
          products={buildServerlessDimensionItems(instanceCostItems.data, base_prices)}
          isLoading={instanceCostItems.isLoading}
          total_cost={instanceCostItems.data ? instanceCostItems.data.total_ecu : 0}
        />
      </EuiFlexGroup>
    )
  }

  const instanceChartQuery = useGetInstanceChartDataV2(
    {
      pathParameters: {
        organization_id: organizationId,
        instance_id: instanceId,
      },
      queryParameters: processQueryParams({
        from: filters.startDate.startOf('day').utc().format(),
        to: filters.endDate.endOf('day').utc().format(),
        bucketing_strategy: ViewByToBucketingStrategy[filters.viewBy],
      }),
    },
    {
      enabled: areDatesValid,
    },
  )

  const renderChart = () => {
    if (instanceChartQuery.isError) {
      return <ErrorCallout retry={instanceChartQuery.refetch} data-test-id='costs-chart-error' />
    }

    const type = instance_type === 'deployment' ? ProductType.Deployments : ProductType.Projects
    return (
      <EuiPanel
        hasShadow={false}
        paddingSize='m'
        hasBorder={true}
        css={costChartPanelCss(theme)}
        data-test-id='costs-chart-panel'
      >
        <CostsChartByInstance
          data={instanceChartQuery.data?.data || []}
          chartType={filters.chartType}
          viewBy={filters.viewBy}
          isLoading={instanceChartQuery.isLoading}
          productType={type}
        />
      </EuiPanel>
    )
  }

  return (
    <Fragment>
      <EuiFlexGroup justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <EuiText size='s'>
            <FormattedMessage id='billing-usage.usage-report' defaultMessage='Usage report for' />
          </EuiText>
          <EuiText size='m' data-test-id='billing-usage.page-title'>
            <h3>{instanceName || instanceId}</h3>
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiLink onClick={onBackToUsageClick} data-test-id='back-to-usage-link'>
            <EuiIcon type='returnKey' />{' '}
            <FormattedMessage
              id='billing-usage.back-to-usage-link'
              defaultMessage='Back to usage'
            />
          </EuiLink>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size='xxl' />

      <MainFilters
        isLoading={instanceCostItems.isLoading}
        organizationId={organizationId}
        shouldShowServerlessFilters={false}
      />

      <EuiSpacer size='m' />

      {renderChart()}

      <EuiSpacer size='l' />

      <EuiText size='s'>
        <FormattedMessage
          id='billing-deployment-usage-list.description'
          defaultMessage='All cost units are measured in {ecu}.'
          values={{
            ecu: (
              <DocLink link='billingECU' favorSaasContext={true}>
                <FormattedMessage
                  id='ecu-tooltip.page-ecu-unit'
                  defaultMessage='Elastic Consumption Units (ECU)'
                />
              </DocLink>
            ),
          }}
        />
      </EuiText>

      <EuiSpacer size='xs' />
      {renderListTable()}
    </Fragment>
  )
}

export default withFiltersProvider<AllProps>(BillingUsageByInstance)
